/**
 * Platform Service — abstract interfaces for platform-specific operations.
 *
 * The editor-core package is platform-agnostic. Each host app (Editor/Tauri,
 * Web/SaaS) provides its own implementation of these interfaces.
 *
 * Usage:
 *   1. Host app creates its platform service implementation
 *   2. Calls `setPlatformService(impl)` at startup
 *   3. editor-core modules call `getPlatformService()` to access platform APIs
 */

import type {
    ConfigGetParams,
    ConfigGetResult,
    ConfigGetSchemaParams,
    ConfigReloadParams,
    ConfigReloadResult,
    ConfigSchemaResult,
    ConfigSetParams,
    ConfigSetResult,
    EngineNotification,
    ExecutionCancelParams,
    ExecutionGetStateParams,
    ExecutionGetStateResult,
    ExecutionStartParams,
    ExecutionStartResult,
    HistoryDeleteParams,
    HistoryDeleteResult,
    HistoryDiffParams,
    HistoryDiffResult,
    HistoryGetParams,
    HistoryGetResult,
    HistoryListParams,
    HistoryListResult,
    HistoryPruneParams,
    HistoryPruneResult,
    HistoryRestoreParams,
    HistoryRestoreResult,
    HistorySnapshotParams,
    HistorySnapshotResult,
    HistoryTagParams,
    HistoryTagResult,
    InitializeResult,
    IntelligenceCodeActionParams,
    IntelligenceCodeActionResult,
    IntelligenceCompleteParams,
    IntelligenceCompleteResult,
    IntelligenceDefinitionParams,
    IntelligenceDefinitionResult,
    IntelligenceDiagnosticsParams,
    IntelligenceDiagnosticsResult,
    IntelligenceHoverParams,
    IntelligenceHoverResult,
    IntelligenceInlayHintParams,
    IntelligenceInlayHintResult,
    IntelligenceReferencesParams,
    IntelligenceReferencesResult,
    IntelligenceRenameParams,
    IntelligenceRenameResult,
    IntelligenceSemanticTokensParams,
    IntelligenceSemanticTokensResult,
    IntelligenceSymbolsParams,
    IntelligenceSymbolsResult,
    ExtensionActivateRuntimeParams,
    ExtensionActivateRuntimeResult,
    ExtensionDisposeRuntimeParams,
    ExtensionDisposeRuntimeResult,
    ExtensionGetManifestParams,
    ExtensionGetManifestResult,
    ExtensionInvokeParams,
    ExtensionInvokeResult,
    ExtensionListResult,
    ExtensionSettingsUpdateParams,
    ExtensionSettingsUpdateResult,
    JobCancelParams,
    JobCancelResult,
    JobGetStateParams,
    JobGetStateResult,
    JobListParams,
    JobListResult,
    McpLogsParams,
    McpLogsResult,
    McpStartParams,
    McpStartResult,
	    McpStatusResult,
	    McpStopResult,
	    PluginApiCallParams,
	    PluginApiCallResult,
	    PluginListResult,
    PublishCancelParams,
    PublishGetStateParams,
    PublishGetStateResult,
    PublishSiteCancelParams,
    PublishSiteGetStateParams,
    PublishSiteGetStateResult,
    PublishSiteStartParams,
    PublishSiteStartResult,
    PublishStartParams,
    PublishStartResult,
    PushConnectParams,
    PushConnectResult,
    PushDisconnectResult,
    PushPairParams,
    PushPairResult,
    PushScanParams,
    PushScanResult,
    PushSendParams,
    PushSendResult,
    PushStatusResult,
    PushSyncStartParams,
    PushSyncStartResult,
    PushSyncStopResult,
    SearchCancelParams,
    SearchQueryParams,
    SearchQueryResult,
    SessionCreateParams,
    SessionCreateResult,
    SessionExplainParams,
    SessionExplainResult,
    SessionGetOutputParams,
    SessionGetOutputResult,
    SessionGetStateParams,
    SessionGetStateResult,
    SessionListResult,
    SessionSyncParams,
    SessionSyncResult,
    SessionUnwatchParams,
    SessionWatchParams,
    SidecarCallStartParams,
    SidecarCallStartResult,
    SidecarCallResult,
    SidecarListResult,
    SidecarSpawnResult,
    SmartIntakeBuildPlanParams,
    SmartIntakeBuildPlanResult,
    SmartIntakeProposeParams,
    SmartIntakeProposeResult,
    WorkspaceAddFolderParams,
    WorkspaceAddFolderResult,
    WorkspaceCopyParams,
    WorkspaceCopyResult,
    WorkspaceExistsParams,
    WorkspaceExistsResult,
    WorkspaceGetInfoParams,
    WorkspaceGetInfoResult,
    WorkspaceListFoldersParams,
    WorkspaceListFoldersResult,
    WorkspaceLoadParams,
    WorkspaceLoadResult,
    WorkspaceMkdirParams,
    WorkspaceMkdirResult,
    WorkspaceMoveParams,
    WorkspaceMoveResult,
    WorkspaceReferenceImpactParams,
    WorkspaceReferenceImpactResult,
    WorkspaceReadDirParams,
    WorkspaceReadDirResult,
    WorkspaceReadFileParams,
    WorkspaceReadFileResult,
    WorkspaceRemoveFolderParams,
    WorkspaceRemoveFolderResult,
    WorkspaceRemoveParams,
    WorkspaceRemoveResult,
    WorkspaceRenameParams,
    WorkspaceRenameResult,
    WorkspaceSaveParams,
    WorkspaceSaveResult,
    WorkspaceSessionGetParams,
    WorkspaceSessionGetResult,
    WorkspaceSessionSetParams,
    WorkspaceSessionSetResult,
    WorkspaceStarAddParams,
    WorkspaceStarAddResult,
    WorkspaceStarListParams,
    WorkspaceStarListResult,
    WorkspaceStarRemoveParams,
    WorkspaceStarRemoveResult,
    WorkspaceStarUpdateParams,
    WorkspaceStarUpdateResult,
    WorkspaceBookmarkAddParams,
    WorkspaceBookmarkAddResult,
    WorkspaceBookmarkListParams,
    WorkspaceBookmarkListResult,
    WorkspaceBookmarkRemoveParams,
    WorkspaceBookmarkRemoveResult,
    WorkspaceBookmarkUpdateParams,
    WorkspaceBookmarkUpdateResult,
    WorkspaceStatParams,
    WorkspaceStatResult,
    WorkspaceWriteFileParams,
    WorkspaceWriteFileResult,
} from "@magic-md/engine-client/protocol";
import type { ConfigEffect, ConfigOwner } from "@magic-md/engine-client/config";
import type { SettingsService } from "./platform/settings-service";

/* ── Engine Service Interface ── */

export interface EngineStatus {
    alive: boolean;
    ready: boolean;
}

export type { EngineNotification };

export type EngineNotificationHandler = (
    notification: EngineNotification,
) => void;

export interface EngineService {
    /** Get engine status */
    getEngineStatus(): Promise<EngineStatus>;

    /** Initialize engine connection */
    initialize(clientInfo: {
        name: string;
        version: string;
    }, capabilities?: import("@magic-md/engine-client/protocol").ClientCapabilities): Promise<InitializeResult>;

    /** Create a session */
    sessionCreate(params: SessionCreateParams): Promise<SessionCreateResult>;

    /** Close a session */
    sessionClose(sessionId: string): Promise<{ success: boolean }>;

    /** Sync session content */
    sessionSync(params: SessionSyncParams): Promise<SessionSyncResult>;

    /** Start an async execution job. */
    executionStart(params: ExecutionStartParams): Promise<ExecutionStartResult>;

    /** Get async execution job state. */
    executionGetState(
        params: ExecutionGetStateParams,
    ): Promise<ExecutionGetStateResult>;

    /** Cancel execution */
    executionCancel(params: ExecutionCancelParams): Promise<{ success: boolean }>;

    /** List runtime jobs across long-running domains. */
    jobList?(params?: JobListParams): Promise<JobListResult>;

    /** Get runtime job state. */
    jobGetState?(params: JobGetStateParams): Promise<JobGetStateResult>;

    /** Cancel any runtime-owned long-running job. */
    jobCancel?(params: JobCancelParams): Promise<JobCancelResult>;

    /** List active sessions */
    sessionList(): Promise<SessionListResult>;

    /** Get full state for a session */
    sessionGetState(
        params: SessionGetStateParams,
    ): Promise<SessionGetStateResult>;

    /** Get one hydrated output wrapper for a session unit */
    sessionGetOutput(
        params: SessionGetOutputParams,
    ): Promise<SessionGetOutputResult>;

    /** Get a runtime explain model for a session or target unit */
    sessionExplain(
        params: SessionExplainParams,
    ): Promise<SessionExplainResult>;

    /** Watch a session's file for external changes (engine sends event/file.changed notifications) */
    sessionWatch(params: SessionWatchParams): Promise<{ success: boolean }>;

    /** Stop watching a session's file */
    sessionUnwatch(params: SessionUnwatchParams): Promise<{ success: boolean }>;

    /** List plugins */
    pluginList(): Promise<PluginListResult>;

    /** Call a renderer-facing plugin runtime API without updating document output. */
    pluginApiCall?<TData = unknown>(
        params: PluginApiCallParams,
    ): Promise<PluginApiCallResult<TData>>;

    /** Ask runtime plugins for Smart Intake offers. */
    smartIntakePropose?(params: SmartIntakeProposeParams): Promise<SmartIntakeProposeResult>;

    /** Ask a runtime plugin to build the selected Smart Intake plan. */
    smartIntakeBuildPlan?(params: SmartIntakeBuildPlanParams): Promise<SmartIntakeBuildPlanResult>;

    /** List sidecars */
    sidecarList(): Promise<SidecarListResult>;

    /** Shutdown a sidecar */
    sidecarShutdown(sidecarId: string): Promise<{ success: boolean }>;

    /** Spawn a sidecar */
    sidecarSpawn(
        sidecarId: string,
        command: string,
        args?: string[],
    ): Promise<SidecarSpawnResult>;

    /** Call a sidecar method (debug/invoke) */
    sidecarCall(
        sidecarId: string,
        method: string,
        params?: Record<string, unknown>,
    ): Promise<SidecarCallResult>;

    /** Start a cancellable async sidecar call. */
    sidecarCallStart?(
        params: SidecarCallStartParams,
    ): Promise<SidecarCallStartResult>;

    /** Start listening for engine notifications */
    listenNotifications(handler: EngineNotificationHandler): Promise<() => void>;

    /** Stop listening */
    stopListening(): void;

    // ── Push Methods ──

    /** Scan for Play devices on the local network */
    pushScan?(params: PushScanParams): Promise<PushScanResult>;

    /** Connect to a Play device */
    pushConnect?(params: PushConnectParams): Promise<PushConnectResult>;

    /** Disconnect from the Play device */
    pushDisconnect?(): Promise<PushDisconnectResult>;

    /** Send pairing code */
    pushPair?(params: PushPairParams): Promise<PushPairResult>;

    /** Push document to connected device */
    pushSend?(params: PushSendParams): Promise<PushSendResult>;

    /** Start real-time sync */
    pushSyncStart?(params: PushSyncStartParams): Promise<PushSyncStartResult>;

    /** Stop real-time sync */
    pushSyncStop?(): Promise<PushSyncStopResult>;

    /** Get push status */
    pushStatus?(): Promise<PushStatusResult>;

    // ── MCP Methods ──

    /** Start the embedded MCP HTTP server */
    mcpStart?(params?: McpStartParams): Promise<McpStartResult>;

    /** Stop the embedded MCP HTTP server */
    mcpStop?(): Promise<McpStopResult>;

    /** Get MCP server status */
    mcpStatus?(): Promise<McpStatusResult>;

    /** Query MCP request logs */
    mcpLogs?(params?: McpLogsParams): Promise<McpLogsResult>;

    // ── Extension Methods (optional) ──

    /** List extensions discovered by the engine. */
    extensionList?(): Promise<ExtensionListResult>;

    /** Get the raw manifest of one extension. */
    extensionGetManifest?(
        params: ExtensionGetManifestParams,
    ): Promise<ExtensionGetManifestResult>;

    /** Activate an extension's runtime (idempotent). */
    extensionActivateRuntime?(
        params: ExtensionActivateRuntimeParams,
    ): Promise<ExtensionActivateRuntimeResult>;

    /** Invoke a runtime handler method. */
    extensionInvoke?(
        params: ExtensionInvokeParams,
    ): Promise<ExtensionInvokeResult>;

    /** Dispose an extension's runtime. */
    extensionDisposeRuntime?(
        params: ExtensionDisposeRuntimeParams,
    ): Promise<ExtensionDisposeRuntimeResult>;

    /** Push a settings update to a runtime. */
    extensionSettingsUpdate?(
        params: ExtensionSettingsUpdateParams,
    ): Promise<ExtensionSettingsUpdateResult>;

    // ── Search Methods ──

    /** 发起搜索，返回 searchId */
    searchQuery?(params: SearchQueryParams): Promise<SearchQueryResult>;

    /** 取消搜索 */
    searchCancel?(params: SearchCancelParams): Promise<{ success: boolean }>;

    // ── Intelligence Methods (optional) ──

    /** Completion suggestions at a position */
    intelligenceComplete?(params: IntelligenceCompleteParams): Promise<IntelligenceCompleteResult>;

    /** Hover info at a position */
    intelligenceHover?(params: IntelligenceHoverParams): Promise<IntelligenceHoverResult | null>;

    /** Diagnostics for a session */
    intelligenceDiagnostics?(params: IntelligenceDiagnosticsParams): Promise<IntelligenceDiagnosticsResult>;

    /** Go to definition from a position */
    intelligenceDefinition?(params: IntelligenceDefinitionParams): Promise<IntelligenceDefinitionResult | null>;

    /** Find all references to a definition */
    intelligenceReferences?(params: IntelligenceReferencesParams): Promise<IntelligenceReferencesResult>;

    /** Rename a definition ID across the document */
    intelligenceRename?(params: IntelligenceRenameParams): Promise<IntelligenceRenameResult>;

    /** Document symbols */
    intelligenceSymbols?(params: IntelligenceSymbolsParams): Promise<IntelligenceSymbolsResult>;

    /** Semantic tokens for full document */
    intelligenceSemanticTokens?(params: IntelligenceSemanticTokensParams): Promise<IntelligenceSemanticTokensResult>;

    /** Code actions at a position */
    intelligenceCodeAction?(params: IntelligenceCodeActionParams): Promise<IntelligenceCodeActionResult>;

    /** Inlay hints for a line range */
    intelligenceInlayHint?(params: IntelligenceInlayHintParams): Promise<IntelligenceInlayHintResult>;

    // ── Config Methods (optional raw RPC access; wrapped by SettingsService) ──

    configGet?(params?: ConfigGetParams): Promise<ConfigGetResult>;
    configSet?(params: ConfigSetParams): Promise<ConfigSetResult>;
    configGetSchema?(params?: ConfigGetSchemaParams): Promise<ConfigSchemaResult>;
    configReload?(params?: ConfigReloadParams): Promise<ConfigReloadResult>;

    // ── Workspace Methods ──

    /** Read directory entries */
    workspaceReadDir(params: WorkspaceReadDirParams): Promise<WorkspaceReadDirResult>;
    /** Read a text file */
    workspaceReadFile(params: WorkspaceReadFileParams): Promise<WorkspaceReadFileResult>;
    /** Write a text file */
    workspaceWriteFile(params: WorkspaceWriteFileParams): Promise<WorkspaceWriteFileResult>;
    /** Get file/directory metadata */
    workspaceStat(params: WorkspaceStatParams): Promise<WorkspaceStatResult>;
    /** Check if a path exists */
    workspaceExists(params: WorkspaceExistsParams): Promise<WorkspaceExistsResult>;
    /** Create directory */
    workspaceMkdir(params: WorkspaceMkdirParams): Promise<WorkspaceMkdirResult>;
    /** Rename or move a file/directory (with optional autoRename) */
    workspaceRename(params: WorkspaceRenameParams): Promise<WorkspaceRenameResult>;
    /** Delete a file or directory */
    workspaceRemove(params: WorkspaceRemoveParams): Promise<WorkspaceRemoveResult>;
    /** Copy a file (with optional autoRename) */
    workspaceCopy(params: WorkspaceCopyParams): Promise<WorkspaceCopyResult>;
    /** Batch move files to a target directory */
    workspaceMove(params: WorkspaceMoveParams): Promise<WorkspaceMoveResult>;
    /** Analyze Magic MD file references affected by workspace file operations */
    workspaceReferenceImpact(params: WorkspaceReferenceImpactParams): Promise<WorkspaceReferenceImpactResult>;
    /** Get workspace info (folders, sep, os, home) */
    workspaceGetInfo(params?: WorkspaceGetInfoParams): Promise<WorkspaceGetInfoResult>;
    /** Add a folder to the workspace */
    workspaceAddFolder(params: WorkspaceAddFolderParams): Promise<WorkspaceAddFolderResult>;
    /** Remove a folder from the workspace */
    workspaceRemoveFolder(params: WorkspaceRemoveFolderParams): Promise<WorkspaceRemoveFolderResult>;
    /** List all workspace folders */
    workspaceListFolders(params?: WorkspaceListFoldersParams): Promise<WorkspaceListFoldersResult>;
    /** Save workspace folders to a .mdspace file */
    workspaceSave(params: WorkspaceSaveParams): Promise<WorkspaceSaveResult>;
    /** Load workspace folders from a .mdspace file */
    workspaceLoad(params: WorkspaceLoadParams): Promise<WorkspaceLoadResult>;
    /** List starred (收藏) entries from current workspace */
    workspaceStarList(params?: WorkspaceStarListParams): Promise<WorkspaceStarListResult>;
    /** Add a file or folder to starred list */
    workspaceStarAdd(params: WorkspaceStarAddParams): Promise<WorkspaceStarAddResult>;
    /** Remove an entry from starred list */
    workspaceStarRemove(params: WorkspaceStarRemoveParams): Promise<WorkspaceStarRemoveResult>;
    /** Update a starred entry (rename label, etc.) */
    workspaceStarUpdate(params: WorkspaceStarUpdateParams): Promise<WorkspaceStarUpdateResult>;
    /** List bookmarks (line-level) from current workspace */
    workspaceBookmarkList(params?: WorkspaceBookmarkListParams): Promise<WorkspaceBookmarkListResult>;
    /** Add a bookmark at the given path:line */
    workspaceBookmarkAdd(params: WorkspaceBookmarkAddParams): Promise<WorkspaceBookmarkAddResult>;
    /** Remove a bookmark by id */
    workspaceBookmarkRemove(params: WorkspaceBookmarkRemoveParams): Promise<WorkspaceBookmarkRemoveResult>;
    /** Update a bookmark (label/note/line) */
    workspaceBookmarkUpdate(params: WorkspaceBookmarkUpdateParams): Promise<WorkspaceBookmarkUpdateResult>;
    /** Get current session state (open tabs etc.) */
    workspaceSessionGet(params?: WorkspaceSessionGetParams): Promise<WorkspaceSessionGetResult>;
    /** Update current session state and optionally persist to .mdspace */
    workspaceSessionSet(params: WorkspaceSessionSetParams): Promise<WorkspaceSessionSetResult>;

    // ── Publish Methods ──

    /** Start publishing the current document */
    publishStart(params: PublishStartParams): Promise<PublishStartResult>;

    /** Get publish task state */
    publishGetState(params: PublishGetStateParams): Promise<PublishGetStateResult>;

    /** Cancel a publish task */
    publishCancel(params: PublishCancelParams): Promise<{ success: boolean }>;

    /** Start site-level (multi-document) publish */
    publishSiteStart(params: PublishSiteStartParams): Promise<PublishSiteStartResult>;

    /** Get site publish task state */
    publishSiteGetState(params: PublishSiteGetStateParams): Promise<PublishSiteGetStateResult>;

    /** Cancel a site publish task */
    publishSiteCancel(params: PublishSiteCancelParams): Promise<{ success: boolean }>;

    // ── History (Snapshots) Methods ──

    /** Take a snapshot of the document */
    historySnapshot(params: HistorySnapshotParams): Promise<HistorySnapshotResult>;
    /** List snapshots for a file */
    historyList(params: HistoryListParams): Promise<HistoryListResult>;
    /** Get a specific snapshot's content */
    historyGet(params: HistoryGetParams): Promise<HistoryGetResult>;
    /** Compute unified diff between two snapshots (or snapshot vs current) */
    historyDiff(params: HistoryDiffParams): Promise<HistoryDiffResult>;
    /** Restore document content from a snapshot */
    historyRestore(params: HistoryRestoreParams): Promise<HistoryRestoreResult>;
    /** Delete a snapshot by id */
    historyDelete(params: HistoryDeleteParams): Promise<HistoryDeleteResult>;
    /** Set / clear a tag on a snapshot */
    historyTag(params: HistoryTagParams): Promise<HistoryTagResult>;
    /** Prune old snapshots according to retention policy */
    historyPrune(params: HistoryPruneParams): Promise<HistoryPruneResult>;
}

/* ── Combined Platform Service ── */

export interface PlatformService {
    engine: EngineService;
    settings: SettingsService;
    /** Open a URL or file path in the system's default application (browser, finder, etc.) */
    openExternal?(url: string): Promise<void>;
    /**
     * Convert a local absolute file path to a URL the renderer can load
     * directly (e.g. via `<img src>`/`<video src>`/`<iframe src>`).
     *
     * - Editor (Tauri): returns `asset://localhost/<encoded>` via `convertFileSrc`.
     * - Web/Play (no local FS): returns `undefined` — caller should show a fallback.
     */
    getAssetUrl?(filePath: string): string | undefined;
    /**
     * Open an extension-owned floating panel in a host-native window when the
     * platform supports it. Browser/Web hosts omit this and editor-core falls
     * back to the in-app floating panel renderer.
     */
    openExtensionFloatingPanel?(
        params: ExtensionFloatingPanelWindowRequest,
    ): string | null | undefined;
    /** Close a host-native extension floating panel, if it is still open. */
    closeExtensionFloatingPanel?(instanceId: string): Promise<void> | void;
    /** Open an extension-contributed sidebar panel in a host-native window. */
    openExtensionSidebarPanel?(
        params: ExtensionSidebarPanelWindowRequest,
    ): string | null | undefined;
    /** Whether host-native extension panel windows are available in this runtime. */
    supportsExtensionPanelWindows?(): boolean;
    /** Whether the current platform can apply this non-immediate config effect right now. */
    canApplyConfigEffectNow?(params: {
        owner: ConfigOwner;
        key: string;
        effect: ConfigEffect;
    }): boolean;
    /** Try to apply a non-immediate config effect right now (restart/reconnect/restart sidecar). */
    applyConfigEffectNow?(params: {
        owner: ConfigOwner;
        key: string;
        effect: ConfigEffect;
        value?: unknown;
    }): Promise<{ success: boolean; message?: string }>;
}

export interface ExtensionFloatingPanelWindowRequest {
    kind?: "floating";
    instanceId: string;
    extensionId: string;
    panelId: string;
    title?: string;
    payload?: unknown;
    width: number;
    height: number;
}

export interface ExtensionSidebarPanelWindowRequest {
    kind: "sidebar";
    instanceId: string;
    extensionId: string;
    panelId: string;
    title?: string;
    width: number;
    height: number;
}

export type ExtensionPanelWindowRequest =
    | ExtensionFloatingPanelWindowRequest
    | ExtensionSidebarPanelWindowRequest;

/* ── Global singleton ── */

let _platform: PlatformService | null = null;

/**
 * Set the platform service implementation.
 * Must be called once at app startup before any editor-core module is used.
 */
export function setPlatformService(service: PlatformService): void {
    _platform = service;
}

/**
 * Get the current platform service.
 * Throws if not yet initialized.
 */
export function getPlatformService(): PlatformService {
    if (!_platform) {
        throw new Error(
            "[editor-core] PlatformService not initialized. " +
            "Call setPlatformService() at app startup.",
        );
    }
    return _platform;
}
